/* eslint-disable class-methods-use-this */
/* eslint-disable lines-between-class-members */
import fetch from 'cross-fetch';
import { updateAuthorization } from '../../actions/session';
import configs from '../../constants/configs';
import store from '../../configureStore';
import { sha256 } from './util';

const { getState, dispatch } = store;

class Request {
    /**
     * Helper method to call the convert html endpoint.
     *
     * @param {unknown} opts
     * @return {Promise<{html: string, htmlWithEmbeddedCustomFonts: string}>}
     */
    convertHtml(opts) {
        return this.post(configs.convertHtml, opts, true /* joinExistingRequest */);
    }

    /**
     * @type {Map<string,Promise<*>>}
     */
    currentRequests = new Map();

    post(url, body, joinExistingRequest = false) {
        return this.request({ url, body, method: 'POST' }, joinExistingRequest);
    }

    get(url, joinExistingRequest = false) {
        return this.request({ url, method: 'GET' }, joinExistingRequest);
    }

    put(url, body, joinExistingRequest = false) {
        return this.request({ url, body, method: 'PUT' }, joinExistingRequest);
    }

    custom(url, options, joinExistingRequest = false) {
        return this.request({ url, customOptions: options }, joinExistingRequest);
    }

    /**
     *
     * @param {string} url
     * @param {unknown|undefined} body
     * @param {string} method
     * @param {unknown|undefined} customOptions
     * @param {boolean} joinExistingRequest - If true, will join an existing request with the same url, body, method, and customOptions
     * @return {Promise<*>}
     */
    async request({ url, body, method, customOptions }, joinExistingRequest) {
        if (!joinExistingRequest) {
            return this.doRequest({
                url,
                body,
                method,
                customOptions,
            });
        }

        const payloadHash = await sha256(JSON.stringify(body || {}) + JSON.stringify(customOptions || {}));
        const requestKey = `${method} ${url} ${payloadHash}`;

        const requestInProgressPromise = this.currentRequests.get(requestKey);
        if (requestInProgressPromise !== undefined) {
            return requestInProgressPromise;
        }

        const requestPromise = this.doRequest({
            url,
            body,
            method,
            customOptions,
        });

        this.currentRequests[requestKey] = requestPromise;
        requestPromise.finally(() => {
            delete this.currentRequests[requestKey];
        });
        return requestPromise;
    }

    async doRequest({ url, body, method, customOptions }) {
        let response;
        if (customOptions) {
            response = await fetch(url, {
                ...customOptions,
                headers: {
                    ...customOptions.headers,
                    Authorization: getState().session.authorization,
                },
            });
        } else {
            response = await fetch(url, {
                method,
                headers: {
                    Authorization: getState().session.authorization,
                    'Content-Type': 'application/json',
                },
                // TODO: Tech-debt on create-service: some requests are POST when they should be GET, so it's possible a POST request from pikasso has no body
                ...(body ? { body: JSON.stringify(body) } : {}),
            });
        }

        if (!response.ok) {
            const bodyTxt = await response.text();
            let error = bodyTxt;
            try {
                ({ error } = JSON.parse(bodyTxt));
            } catch (e) {
                // ignore
            }

            throw new Error(error || 'An unknown error occurred');
        }

        this.updateJwt(response);
        // TODO: We can't check for text/html as CS sometimes returns JSON as text/html
        // CS also sometimes returns just 'success', hence the empty catch. Need to make CS return a standard response
        const data = await response.json().catch(() => {});
        // TODO: remove check and destructure above line once CS endpoints are consistent (PIK-1339)
        if (data && data.data) {
            return data.data;
        }
        return data;
    }

    updateJwt(response) {
        const newJwt = response.headers.get('x-token-data');
        const oldJwt = getState().session.authorization;
        const oldTokenFormatted = oldJwt.toLowerCase().includes('bearer') ? oldJwt.split(' ')[1] : oldJwt;
        if (newJwt && newJwt !== oldTokenFormatted) {
            dispatch(updateAuthorization(`Bearer ${newJwt}`));
        }
    }
}

// eslint-disable-next-line import/prefer-default-export
export const request = new Request();
